import uuid from 'utils/uuid';
import {
  getAssetUrl,
  getSampleBookPage,
  getTemplatePage,
  isPhotoPlaceholder,
  isTextPlaceholder,
} from 'editor-utils/template';

/**
 * Generate common Page object
 * @param {object} page
 * @param {object} opts
 * @param {number} opts.pageNumber
 * @returns {object}
 */
export const generatePage = (page, { pageNumber } = {}) => {
  const placeholderData = placeholder => {
    if (isTextPlaceholder(placeholder)) {
      if (placeholder.id === 'page-num' && pageNumber != null) {
        if (Number.isInteger(pageNumber) && pageNumber > 0) {
          return { value: `${pageNumber}` };
        }
      }
    }

    return {};
  };

  return {
    key: uuid(),
    id: page.id,
    tags: page.tags ?? [],
    placeholders: (page.placeholders ?? []).map(placeholder => ({
      id: placeholder.id,
      type: placeholder.type,
      data: placeholderData(placeholder),
    })),
  };
};

/**
 * Transform section (pages or cover) from server to client.
 * @param {object} template
 * @param {string} type
 * @param {Array} originalPhotos
 * @param {Array} pages
 * @returns {Array}
 */
export const transformPagesFromServer = (template, type = 'pages', originalPhotos, pages = []) => {
  const templatePagesIds = template[type].map(page => page.id);

  return pages.map(({ placeholders: generatedPlaceholders, specs, ...page }) => {
    let pageId;
    // page.id should be Array of supported pageIds or String pageId
    if (Array.isArray(page.id)) {
      // when page.id is array, find first supported page from template
      pageId = page.id.find(id => templatePagesIds.indexOf(id) >= 0);
    } else {
      pageId = page.id;
    }

    if (!pageId) {
      // TODO Unknown page
      console.log('Unknown page for this SKU', page.id);
      pageId = templatePagesIds[0];
    }

    const placeholderData = (item = {}) => {
      if (isPhotoPlaceholder(item)) {
        let photoKey = item.photo_key;
        if (!photoKey && item.defaultValue) {
          photoKey = item.defaultValue;
        }

        return {
          photo_key: photoKey,
          x: item.x ?? 0.5,
          y: item.y ?? 0.5,
          scale: item.scale ?? 1,
          orientation: item.orientation ?? 0,
        };
      }

      if (isTextPlaceholder(item)) {
        return {
          value: item.value,
          value_enhanced: item.value,
          value_original: item.value_original || item.value,
        };
      }

      return {};
    };

    const templatePage = getTemplatePage(template, pageId, type);

    const placeholders = templatePage.placeholders.map((placeholder, sortIndex) => ({
      id: placeholder.id,
      type: placeholder.type,
      sortIndex: sortIndex + 0.5,
    }));

    (generatedPlaceholders ?? []).forEach((item, sortIndex) => {
      let placeholderIndex = placeholders.findIndex(
        placeholder => placeholder.id === item.placeholder_id,
      );
      if (placeholderIndex < 0) {
        placeholderIndex =
          placeholders.push({
            id: item.placeholder_id,
            type: item.type,
            sortIndex,
          }) - 1;
      }
      placeholders[placeholderIndex].data = placeholderData(item);

      const placeholderId = placeholders[placeholderIndex].id;

      // TODO spec should be a part of generatedPlaceholder
      const spec = (specs ?? []).find(s => s.id === placeholderId);
      if (spec) {
        const { polotno, ...override } = spec;
        placeholders[placeholderIndex].override = override;
        placeholders[placeholderIndex].polotno = polotno ?? undefined;
      }
    });

    for (let index = placeholders.length - 1; index >= 0; index -= 1) {
      if (!('sortIndex' in placeholders[index])) {
        const nextPlaceholder = placeholders[index + 1] ?? {};
        if ('sortIndex' in nextPlaceholder) {
          placeholders[index].sortIndex = nextPlaceholder.sortIndex - 0.1;
        }
      }
    }

    // sort placeholders, respect generator order, then rest of placeholders.
    placeholders.sort((a, b) => {
      const sortIndexA = a.sortIndex ?? placeholders.length;
      const sortIndexB = b.sortIndex ?? placeholders.length;

      return sortIndexA < sortIndexB ? -1 : 1;
    });

    return {
      ...page,
      id: pageId,
      placeholders: placeholders
        .map(({ sortIndex, ...placeholder }) => placeholder)
        .filter(p => p.id.indexOf('generator') !== 0),
      tags: page.tags ?? [],
    };
  });
};

/**
 * Transform server generated data to client data.
 * @param {object} template
 * @param {Array} photos
 * @param {object} data
 * @returns {Array}
 */
export const transformPages = (template, photos, data) => {
  if (template.cover) {
    const pages = [
      ...transformPagesFromServer(template, 'cover', photos, [data.cover]),
      ...transformPagesFromServer(template, 'pages', photos, data.pages),
    ];

    // When number of [cover, ...pages] is even, add new page, this number has to be odd
    if (pages.length % 2 === 0) {
      pages.push(generatePage(getSampleBookPage(template)));
    }

    return pages;
  }

  return transformPagesFromServer(template, 'pages', photos, data.pages);
};

/**
 * Extract photos from server generated pages
 * @param {object} template
 * @param {Array} pages
 * @returns {Array}
 */
export const extractPhotosFromPages = (template, pages = []) => {
  const photos = [];

  pages.forEach(page => {
    (page.placeholders || []).forEach(placeholder => {
      if (isPhotoPlaceholder(placeholder)) {
        let defaultValue;

        let { photo_key: key, photo_url: url } = placeholder;
        if (!url && placeholder.defaultValue) {
          defaultValue = placeholder.defaultValue;
          key = defaultValue;
          url = getAssetUrl(template, `/assets_client/images/${defaultValue}`);
        }

        photos.push({
          key,
          url,
          width: placeholder.width || undefined,
          height: placeholder.height || undefined,
          alive: 1,
          datetime: 'N/A',
          tags: placeholder.tags || [],
          type: placeholder.photo_mime_type || undefined,
          defaultValue,
        });
      }
    });
  });

  return photos;
};

/**
 * Generate new page, based on previous one.
 * @param {object} template
 * @param {object} previousPage
 * @param {string} newPageId
 * @param {string} type
 * @returns {object}
 */
export function generateUpdatedPage(template, previousPage, newPageId, type = 'pages') {
  const templatePage = getTemplatePage(template, newPageId, type);

  return {
    ...previousPage,
    id: newPageId,
    placeholders: templatePage.placeholders.map(placeholder => {
      // We need to use some data from previousPage
      let data = {};

      const previousPlaceholder = previousPage.placeholders.find(p => p.id === placeholder.id);
      if (previousPlaceholder?.data) {
        data = {
          ...previousPlaceholder.data,
        };

        // Reset photo data when layout is changed
        if (isPhotoPlaceholder(placeholder)) {
          data = {
            ...data,
            x: 0.5,
            y: 0.5,
            scale: 1,
          };
        }
      }

      return {
        id: placeholder.id,
        type: placeholder.type,
        data,

        // TODO is usefull to keep override from previous placeholder?
        // override: placeholder.override
      };
    }),
  };
}
